import { Route, Routes } from "react-router-dom";
import { PatientLayout } from "@/components/patient/PatientLayout";
import { PatientDashboard } from "@/components/patient/PatientDashboard";
import { BookingHistory } from "@/components/patient/BookingHistory";
import { PatientBooking } from "@/components/PatientBooking";
import { PatientProfile } from "@/components/patient/PatientProfile";
import { TriageForm } from "@/components/triage/TriageForm";
import { PatientDocuments } from "@/components/patient/PatientDocuments";
import { HelpOptionsScreen } from "@/components/patient/HelpOptionsScreen";
import { AudioTriageWelcome } from "@/components/patient/AudioTriageWelcome";
import { AudioSymptomCollector } from "@/components/patient/AudioSymptomCollector";
import FollowUpQuestionScreen from "@/components/patient/FollowUpQuestionScreen";
import { AudioTriageResultScreen } from "@/components/patient/AudioTriageResultScreen";
import { UrgentCareConnect } from "@/components/patient/UrgentCareConnect";
import { AudioTriageProviderSelect } from "@/components/patient/AudioTriageProviderSelect";
import { AudioTriageBookingConfirm } from "@/components/patient/AudioTriageBookingConfirm";
import { AudioTriageProvider } from "@/contexts/AudioTriageContext";
import { ConsentProvider } from "@/contexts/ConsentContext";
import { GlobalConsentManager } from "@/components/consent/GlobalConsentManager";
import { ProtectedRoute } from "@/components/consent/ProtectedRoute";

/**
 * Patient Routes Component
 * 
 * This component defines all routes available to patient users within the application.
 * It uses the PatientLayout component to provide a consistent UI structure with:
 * - Persistent navigation with active states
 * - Breadcrumb navigation
 * - Health snapshot sidebar/header
 */
export function PatientRoutes() {
  return (
    <ConsentProvider>
      {/* Global consent management - handles all consent modals centrally */}
      <GlobalConsentManager />
      <Routes>
        {/* New entry point screen - does not use PatientLayout */}
        <Route index element={<HelpOptionsScreen />} /> 
        <Route path="help" element={<HelpOptionsScreen />} />
        
        {/* Audio Triage Routes - AI-powered features protected by ProtectedRoute */}
        <Route path="audio-triage/welcome" element={
          <ProtectedRoute 
            requiredPurposes={['ai_symptom_assessment', 'clinical_ai_assistance']}
            showLoadingState={true}
          >
            <AudioTriageProvider>
              <AudioTriageWelcome />
            </AudioTriageProvider>
          </ProtectedRoute>
        } />
        <Route path="audio-triage/symptoms" element={
          <ProtectedRoute 
            requiredPurposes={['ai_symptom_assessment', 'clinical_ai_assistance']}
            showLoadingState={true}
          >
            <AudioTriageProvider>
              <AudioSymptomCollector />
            </AudioTriageProvider>
          </ProtectedRoute>
        } />
        <Route path="audio-triage/question" element={
          <ProtectedRoute 
            requiredPurposes={['ai_symptom_assessment', 'clinical_ai_assistance']}
            showLoadingState={true}
          >
            <AudioTriageProvider>
              <FollowUpQuestionScreen />
            </AudioTriageProvider>
          </ProtectedRoute>
        } />
        <Route path="audio-triage/results" element={
          <ProtectedRoute 
            requiredPurposes={['ai_symptom_assessment', 'clinical_ai_assistance']}
            showLoadingState={true}
          >
            <AudioTriageProvider>
              <AudioTriageResultScreen />
            </AudioTriageProvider>
          </ProtectedRoute>
        } />
        <Route path="audio-triage/urgent-connect" element={
          <AudioTriageProvider>
            <UrgentCareConnect />
          </AudioTriageProvider>
        } />
        <Route path="audio-triage/book/provider-select" element={
          <AudioTriageProvider>
            <AudioTriageProviderSelect />
          </AudioTriageProvider>
        } />
        <Route path="audio-triage/book/confirm" element={
          <AudioTriageProvider>
            <AudioTriageBookingConfirm />
          </AudioTriageProvider>
        } />

        {/* Routes within the main patient application layout */}
        <Route element={<PatientLayout />}>
          {/* Dashboard Route - AI-powered features protected by ProtectedRoute */}
          <Route path="dashboard" element={
            <ProtectedRoute 
              requiredPurposes={['ai_symptom_assessment', 'clinical_ai_assistance']}
              showLoadingState={true}
            >
              <PatientDashboard />
            </ProtectedRoute>
          } /> 
          {/* Appointments */}
          <Route path="appointments">
            <Route index element={<BookingHistory />} />
            <Route path="history" element={<BookingHistory />} />
            <Route path="book" element={<PatientBooking />} />
          </Route>
          
          {/* Health Records */}
          <Route path="records">
            <Route index element={<PatientDocuments />} />
          </Route>
          
          {/* Triage Form - AI-powered assessment protected by ProtectedRoute */}
          <Route path="triage" element={
            <ProtectedRoute 
              requiredPurposes={['ai_symptom_assessment', 'clinical_ai_assistance']}
              showLoadingState={true}
            >
              <TriageForm />
            </ProtectedRoute>
          } />
          
          {/* Profile */}
          <Route path="profile" element={<PatientProfile />} />
        </Route>
      </Routes>
    </ConsentProvider>
  );
}
